import {useState, useEffect, useMemo, Fragment} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
    Grid,
    Breadcrumbs,
    Typography,
    Button,
    Stack,
    IconButton,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper, Chip, CircularProgress, Box, LinearProgress,
} from '@mui/material';
import {Add, Category, Home as HomeIcon, Visibility, Warning} from '@mui/icons-material';
import {CheckListIcon, EditIcon, TrashIcon} from '../../../common/Icons';
import { checkPermissionsAndRedirect, hasPermission } from '../../../../configs/functions';
import {atualizaGHEs, deletaGHEs, listaGHEs, relatorioGHEs} from './config/functions';
import { listaEmpresas } from '../../management/companies/configs/functions';
import LinkNavegacao from '../../../common/Link';
import ConfirmDialog from '../../../common/ConfirmDialog';
import PageTitle from '../../../common/PageTitle';
import { FiltersButton } from '../../../common/FiltersButton';
import { useAppContext } from '../../../../contexts/AppContext';
import { useCommonItems } from '../../../../contexts/CommonItensProvider';
import { GHETableFilter } from './components/GHETableFilter';
import GHEForm from './GHEForm';
import { useModuleContext } from '../../../../contexts/ModuleProvider';
import { extractErrorMessage } from '../../../../utils/extract-error-message';
import {Pagination} from "../../../common/Pagination";
import {EmptyTableRow} from "../../../common/EmptyTableRow";
import {SkeletonTableRow} from "../../../common/SkeletonTableRow";
import {CustomToolbarExportTable} from "../../../common/CustomToolbarExportTable";
import "jspdf-autotable";
import {exportGHEsToCSV, exportGHEsToExcel, exportGHEsToPDF} from "./config/utils";
import {listaCategorias} from "../products/configs/functions";
import { listaUsuariosFilter } from '../../management/people/configs/functions';
import {descricaoBloqueio, isGrupoTravado, mensagemGrupoTravado} from "./config/processamentos";
import {Tooltip} from "recharts";
import {useProcessamentos} from "../../../../contexts/ProcessamentosProvider";
import {useGruposEmProcessamento} from "./config/gruposEmProcessamento";
import {alpha} from "@mui/material/styles";

function AcaoBloqueavel({ bloqueado, titulo, children }) {
    if (!bloqueado) return children;

    return (
        <Tooltip title={titulo} arrow>
            <span>{children}</span>
        </Tooltip>
    );
}

const GheRow = ({
                    data,
                    processamento,
                    setOpenCategoryDetailDialog,
                    setOpenForm,
                    actionsAllowed,
                    handleOpenEditForm,
                    handleUpdateStatus,
                    handleOpenDeleteDialog,
                }) => {
    const { exibirAlerta } = useCommonItems();

    const [openStatusDialog, setOpenStatusDialog] = useState(false);
    const [checkingPeople, setCheckingPeople] = useState(false);
    const [openActiveStatusDialog, setOpenActiveStatusDialog] = useState(false);

    const canUpdate = hasPermission(["admin", "admin_grupos", "update_grupos"]);
    const canDelete = hasPermission(["admin", "admin_grupos", "delete_grupos"]);

    const bloqueado = !!processamento;
    const bloqueiaEdicao = bloqueado && !!actionsAllowed;
    const mensagemBloqueio = descricaoBloqueio(processamento);

    async function handleToggleStatus() {
        if (bloqueado) return;

        if (!data.status) {
            setOpenActiveStatusDialog(true);
            return;
        }

        setCheckingPeople(true);
        try {
            const res = await listaUsuariosFilter({ id_grupo: data.id, isFilter: true });
            const pessoas = res?.data?.data ?? [];

            if (pessoas.length > 0) {
                exibirAlerta(
                    'Atenção',
                    `Não é possível desativar o grupo "${data.nome}", pois ele possui pessoas vinculadas.`,
                    'warning'
                );
                return;
            }
            setOpenStatusDialog(true);
        } catch (error) {
            exibirAlerta('Erro', 'Não foi possível verificar as pessoas vinculadas ao grupo.', 'error');
        } finally {
            setCheckingPeople(false);
        }
    }

    async function confirmInactivate() {
        try {
            await handleUpdateStatus();
        } finally {
            setOpenStatusDialog(false);
        }
    }

    async function confirmActive() {
        try {
            await handleUpdateStatus();
        } finally {
            setOpenActiveStatusDialog(false);
        }
    }

    return (
        <>
            <TableRow
                sx={bloqueado ? (theme) => ({
                    bgcolor: alpha(theme.palette.primary.main, 0.07),
                    '& > td:first-of-type': { boxShadow: `inset 4px 0 0 ${theme.palette.primary.main}` },
                }) : undefined}
            >
                <TableCell>
                    {bloqueado ? (
                        <Box>
                            <Typography variant="body2" fontWeight={600}>{data.nome}</Typography>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75 }}>
                                <LinearProgress
                                    variant={processamento.progresso == null ? 'indeterminate' : 'determinate'}
                                    value={processamento.progresso ?? 0}
                                    sx={{ width: 140, height: 6, borderRadius: 3 }}
                                />
                                <Typography variant="caption" color="primary" fontWeight={600} noWrap>
                                    Em processamento{processamento.progresso != null ? ` · ${processamento.progresso}%` : ''}
                                </Typography>
                            </Stack>
                        </Box>
                    ) : data.nome}
                </TableCell>
                <TableCell>{data.cod_externo}</TableCell>
                <TableCell
                    sx={{ maxWidth: 450, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                >
                    {data.observacao}
                </TableCell>
                <TableCell>
                    <AcaoBloqueavel bloqueado={bloqueado} titulo={mensagemBloqueio}>
                        <Switch
                            checked={!!data.status}
                            onChange={handleToggleStatus}
                            disabled={!canUpdate || !actionsAllowed || checkingPeople || bloqueado}
                        />
                    </AcaoBloqueavel>
                </TableCell>
                <TableCell align="right" sx={{ minWidth: 110 }}>
                    {bloqueado ? (
                        <Tooltip title={mensagemBloqueio} arrow>
                            <Chip
                                color="primary"
                                icon={<CircularProgress size={14} thickness={6} color="inherit" />}
                                label="Processando"
                                sx={{ fontWeight: 700, '& .MuiChip-icon': { color: 'inherit' } }}
                            />
                        </Tooltip>
                    ) : (
                        <>
                            <IconButton
                                size="small"
                                onClick={() => {
                                    setOpenForm(true);
                                    setOpenCategoryDetailDialog(true);
                                    handleOpenEditForm(data, { somenteVisualizar: true });
                                }}
                            >
                                <Category color="primary" />
                            </IconButton>
                            {canUpdate && (
                                <IconButton aria-label="Editar" onClick={() => handleOpenEditForm(data)}>
                                    {actionsAllowed ? <EditIcon /> : <Visibility color="primary" />}
                                </IconButton>
                            )}
                            {canDelete && actionsAllowed && (
                                <IconButton aria-label="Excluir" onClick={handleOpenDeleteDialog}>
                                    <TrashIcon />
                                </IconButton>
                            )}
                        </>
                    )}
                </TableCell>
            </TableRow>

            <ConfirmDialog
                title="Tem certeza que deseja inativar este grupo?"
                description={
                    'Ao inativar o grupo, as permissões são desativadas e o estoque dos colaboradores vinculados é zerado. ' +
                    'Uma futura reativação exigirá a liberação manual de cada permissão.'
                }
                state={openStatusDialog}
                goAction={confirmInactivate}
                handleClose={() => setOpenStatusDialog(false)}
                icon={<Warning fontSize='large' />}
            />

            <ConfirmDialog
                title="Tem certeza que deseja ativar este grupo?"
                description={
                    'O grupo será reativado e o estoque será processado em segundo plano. ' +
                    'Enquanto isso, ele ficará bloqueado para edição — você pode acompanhar o andamento pelo aviso no topo da tela. ' +
                    'Lembre-se: as permissões precisam ser liberadas manualmente depois da ativação.'
                }
                state={openActiveStatusDialog}
                goAction={confirmActive}
                handleClose={() => setOpenActiveStatusDialog(false)}
                icon={<Warning fontSize='large' />}
            />
        </>
    );
};

function ListOfGHEs() {
    const queryClient = useQueryClient();

    const { exibirAlerta } = useCommonItems();
    const { getEmpresaIdSession, setEmpresaIdSession } = useAppContext();
    const { verifyIntegrationMode } = useModuleContext();
    const { dadosUsuario } = useAppContext();

    const [moduleActionsAllowed, setModuleActionsAllowed] = useState(null);

    const [searchParams, setSearchParams] = useSearchParams();

    const searchParamPage = searchParams.get('page');
    const searchParamPerPage = searchParams.get('perPage');

    const page = searchParamPage ? (parseInt(searchParamPage) - 1) : 0;
    const rowsPerPage = searchParamPerPage ? parseInt(searchParamPerPage) : 10;

    const [isOpenFilters, setIsOpenFilters] = useState(false);
    const [activeFilters, setActiveFilters] = useState(0);

    const idEmpresaSelecionada = searchParams.get('id_empresa');
    const { acompanhar } = useProcessamentos();
    const gruposEmProcessamento = useGruposEmProcessamento(idEmpresaSelecionada);

    const [openForm, setOpenForm] = useState(false)
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [openCategoryDetailDialog, setOpenCategoryDetailDialog] = useState(false);

    const [selectedGHE, setSelectedGHE] = useState(null);

    const columns = useMemo(() => [
        { field: 'nome', headerName: 'Grupo (GHE)' },
        { field: 'cod_externo', headerName: 'Cód. Externo' },
        { field: 'descricao', headerName: 'Descrição' },
        { field: 'observacao', headerName: 'Observação' },
        { field: 'status', headerName: 'Status', valueGetter: (params) => params.row.status === 1 ? 'Ativo' : 'Inativo' },
    ], []);

    const { data: empresasData, error: listaEmpresasError } = useQuery({
        queryKey: ['listaEmpresas'],
        queryFn: async () => {
            const response = await listaEmpresas(null, null, null, true);
            const empresasData = response.data.data;

            const selectedEmpresaId = getEmpresaIdSession() || empresasData[0]?.id;

            setEmpresaIdSession(selectedEmpresaId);

            setSearchParams((state) => {
                state.set('page', String(page + 1));
                state.set('perPage', String(rowsPerPage));
                state.set('id_empresa', String(selectedEmpresaId));

                return state;
            });
            return response;
        },
        refetchOnWindowFocus: false,
    });

    const queryFilters = {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        nome: searchParams.get('nome'),
        cod_externo: searchParams.get('cod_externo') ?? '',
        id_empresa: searchParams.get('id_empresa'),
        id_categoria: searchParams.get('id_categoria') ?? '',
    };

    // const queryKeyListGHEs = ['listOfGHEs', queryFilters];
    // const { data: groupsData, isLoading, isPending, error: listOfGHEsError } = useQuery({
    //     queryKey: queryKeyListGHEs,
    //     queryFn: () => listaGHEs(queryFilters),
    //     enabled: !!searchParamPage && !!searchParamPerPage,
    // });

    const queryKeyGHEsReport = ['reportOfGHEs', queryFilters];
    const { data: reportData, isLoading: isLoadingReport, isPending: isPendingReport, error: reportOfGHEsError } = useQuery({
        queryKey: queryKeyGHEsReport,
        queryFn: () => relatorioGHEs(queryFilters),
        enabled: !!searchParamPage && !!searchParamPerPage,
    });

    const { data: categoriasData } = useQuery({
        queryKey: ['listaCategorias', searchParams.get('id_empresa')],
        queryFn: () => listaCategorias([searchParams.get('id_empresa')]),
        enabled: !!searchParams.get('id_empresa'),
        refetchOnWindowFocus: false,
    });
    const categorias = categoriasData?.data?.data ?? [];

    const processApiData = (apiData) => {
        if (!apiData?.grupos) return [];

        return apiData.grupos.map((grupo) => ({
            id: grupo.idGrupo,
            id_empresa: grupo.idEmpresaGrupo,
            nome: grupo.nomeGrupo,
            cod_externo: grupo.codExternoGrupo,
            descricao: grupo.descricaoGrupo,
            observacao: grupo.observacaoGrupo,
            status: grupo.statusGrupo,
            categorias: grupo.categorias.map((cat) => ({
                id: cat.idGrupoCategoria,
                nome: cat.nomeCategoria,
                periodo: cat.periodoGrupoCategoria,
                ponto_alerta: cat.pontoAlertaGrupoCategoria,
                status: cat.statusGrupoCategoria,
                quantidade_maxima: cat.quantidadeMaximaGrupoCategoria,
                permissoes: cat.permissoes.map((perm) => ({
                    id: perm.idPermissao,
                    periodo: perm.periodoPermissao,
                    ponto_alerta: perm.pontoAlertaPermissao,
                    info_categoria: perm.infoCategoriaPermissao,
                    status: perm.statusPermissao,
                    produto: perm.idProduto
                        ? {
                            id: perm.idProduto,
                            nome: perm.nomeProduto,
                            cod_externo: perm.codExternoProduto,
                            status: perm.statusProduto,
                            cod_externo_filhos: perm.codExternoFilhos,
                        }
                        : null,
                })),
            })),
        }));
    };

    const numTotalItems = reportData?.data?.numero_total ?? 0;
    const isLoadingList = isLoadingReport || isPendingReport;

    const ghesReport = processApiData(reportData?.data);

    const { mutateAsync: handleUpdateStatusFn } = useMutation({
        mutationFn: (data) => atualizaGHEs(data.id, data.dataToUpdate),
        onSuccess: (response, variables) => {
            const newStatus = variables.dataToUpdate.status;

            const ativou = !!variables.dataToUpdate.status;

            exibirAlerta(
                'Sucesso',
                ativou
                    ? 'Grupo ativado. O estoque está sendo processado em segundo plano.'
                    : 'Grupo inativado com sucesso.',
                'success'
            );

            acompanhar({
                id: response.data?.id_processamento,
                id_grupo: variables.id,
                nome_grupo: variables.nome,
                id_empresa: idEmpresaSelecionada,
                acao: 'status',
            });

            queryClient.invalidateQueries({ queryKey: queryKeyGHEsReport });
        },
        onError: (error) => {
            if (isGrupoTravado(error)) {
                queryClient.invalidateQueries({ queryKey: ['processamentosAtivos'] });
                exibirAlerta('Grupo em processamento', error.response?.data?.message ?? 'Aguarde o processamento terminar para excluir.', 'warning');
                return;
            }

            const message = error.response?.data?.message ?? 'Erro ao executar operação';
            exibirAlerta('Erro', message, 'error');
        },
    });

    const { mutateAsync: handleDelete } = useMutation({
        mutationFn: deletaGHEs,
        onSuccess: (response, id) => {
            const message = response.data.message ?? 'Grupo excluído com sucesso.';
            exibirAlerta('Sucesso', message, 'success');
            queryClient.invalidateQueries({ queryKey: queryKeyGHEsReport });
        },
        onError: (error) => {
            const message = error.response?.data?.message ?? 'Erro ao executar operação';
            exibirAlerta('Erro', message, 'error');
        },
        onSettled: () => {
            setSelectedGHE(null);
            setOpenDeleteDialog(false);
        },
    });

    function handleEditGHE(item, { somenteVisualizar = false } = {}) {
        const processamento = gruposEmProcessamento.get(Number(item.id));

        if (processamento && moduleActionsAllowed && !somenteVisualizar) {
            exibirAlerta('Grupo em processamento', descricaoBloqueio(processamento), 'warning');
            return;
        }

        setSelectedGHE(item);
        setOpenForm(true);
    }

    const handleOpenDeleteDialog = (item) => {
        setSelectedGHE(item);
        setOpenDeleteDialog(true);
    }

    useEffect(() => {
        if (openForm) return;
        setSelectedGHE(null);

        const queryFilters = { ...Object.fromEntries(searchParams) }

        const tableFilters = { ...queryFilters };
        delete tableFilters.page;
        delete tableFilters.perPage;

        const activeFilters = Object.keys(tableFilters).length;

        setActiveFilters(activeFilters);
        setIsOpenFilters(activeFilters > 0);

        const empresaHasActionsAllowed = verifyIntegrationMode('Grupo', searchParams.get('id_empresa'));
        setModuleActionsAllowed(empresaHasActionsAllowed);
    }, [openForm, searchParams, verifyIntegrationMode]);

    useEffect(() => {
        const editar = searchParams.get('editar');

        if (!editar || isLoadingList) return;

        const item = ghesReport.find((grupo) => String(grupo.id) === editar);

        setSearchParams((state) => {
            state.delete('editar');
            return state;
        }, { replace: true });

        if (item) handleEditGHE(item);
    }, [searchParams, isLoadingList, ghesReport]);

    useEffect(() => {
        const requiredPermissionsView = ["admin", "admin_grupos", "list_grupos"];
        checkPermissionsAndRedirect(requiredPermissionsView);
    }, []);

    useEffect(() => {
        let errorMessage = "";

        if (reportOfGHEsError) {
            errorMessage = extractErrorMessage(reportOfGHEsError, 'Erro ao carregar a GHEs');
        }

        if (listaEmpresasError) {
            errorMessage = extractErrorMessage(listaEmpresasError, 'Erro ao carregar a lista de empresas');
        }

        errorMessage && exibirAlerta('Ops! Ocorreu um erro', errorMessage, 'error');
    }, [reportOfGHEsError, listaEmpresasError, exibirAlerta]);

    return (<>
        <Helmet title='Grupos Homogêneos de Exposição' defer={false} />

        <Grid container direction="row" justifyContent="flex-start" alignItems="stretch" spacing={3}>
            <Grid item xs={12}>
                <Breadcrumbs>
                    <LinkNavegacao to="/"><HomeIcon fontSize="small" /></LinkNavegacao>
                    <LinkNavegacao to="/epis">Gestão de EPI's</LinkNavegacao>
                    <LinkNavegacao to="/epis/diretrizes">Diretrizes</LinkNavegacao>
                    <Typography variant="span">Grupos Homogêneos de Exposição</Typography>
                </Breadcrumbs>

                <PageTitle
                    icon={<CheckListIcon fontSize='large' />}
                    title={'Grupos Homogêneos de Exposição'}
                    description='Administre os Grupos Homogêneos de Exposição das suas empresas.'
                />
            </Grid>

            <Grid item xs={12}>
                <Stack direction={{ xs: 'column-reverse', md: 'row' }} spacing={2} justifyContent='space-between' alignItems='center'>
                    <FiltersButton
                        isOpenFilters={isOpenFilters}
                        setIsOpenFilters={setIsOpenFilters}
                        activeFilters={activeFilters}
                    />

                    {hasPermission(["admin", "admin_grupos", "create_grupos"]) &&
                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={() => setOpenForm(true)}
                        >
                            Cadastrar novo GHE
                        </Button>
                    }
                </Stack>
            </Grid>

            <GHETableFilter
                isOpenFilters={isOpenFilters}
                isLoadingList={isLoadingList}
                empresas={empresasData?.data?.data || []}
                categorias={categorias}
            />

            <Grid item xs={12}>
                <TableContainer component={Paper}>
                    <CustomToolbarExportTable
                        rows={ghesReport}
                        columns={columns}
                        reportName="relatorio_grupos_homogeneos_exposicao"
                        reportPdfTitle="RELATÓRIO DE GRUPOS HOMOGÊNEOS DE EXPOSIÇÃO"
                        getDataWithoutPaginate={() => {
                            const { limit, offset, ...data } = queryFilters;
                            return listaGHEs(data);
                        }}
                        getSelectedEmpresa={() =>
                            empresasData.data.data.find(
                                empresa => empresa.id === Number(searchParams.get('id_empresa'))
                            )
                        }
                        handleCustomPdf={async () => {
                            const selectedEmpresa = empresasData.data.data.find(
                                empresa => empresa.id === Number(searchParams.get('id_empresa'))
                            );

                            exportGHEsToPDF({
                                tableData: ghesReport,
                                columns,
                                selectedEmpresa,
                                reportName: "relatorio_grupos_homogeneos_exposicao",
                                reportTitle: "RELATÓRIO DE GRUPOS HOMOGÊNEOS DE EXPOSIÇÃO",
                                dadosUsuario
                            });
                        }}
                        handleCustomExportCsv={async () => {
                            exportGHEsToCSV({
                                tableData: ghesReport,
                                reportName: "relatorio_grupos_homogeneos_exposicao",
                            });
                        }}
                        handleCustomExportExcel={async () => {
                            exportGHEsToExcel({
                                tableData: ghesReport,
                                reportName: "relatorio_grupos_homogeneos_exposicao",
                            });
                        }}
                    />

                    <Table aria-label="Grupos Homogêneos de Exposição">
                        <TableHead>
                            <TableRow>
                                <TableCell>Nome</TableCell>
                                <TableCell>Cód. Externo</TableCell>
                                <TableCell>Observação</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoadingList ? (
                                <SkeletonTableRow numCells={5} numRows={rowsPerPage < 20 ? rowsPerPage : 5} />
                            ) : (
                                ghesReport && ghesReport?.length > 0 ? (
                                    ghesReport.map((item) => (
                                        <GheRow
                                            key={item.id}
                                            data={item}
                                            setOpenCategoryDetailDialog={setOpenCategoryDetailDialog}
                                            setOpenForm={setOpenForm}
                                            handleOpenEditForm={handleEditGHE}
                                            handleUpdateStatus={() =>
                                                handleUpdateStatusFn({
                                                    id: item.id,
                                                    nome: item.nome,
                                                    dataToUpdate: { status: !item.status },
                                                }).catch(() => {})
                                            }
                                            handleOpenDeleteDialog={() => handleOpenDeleteDialog(item)}
                                            actionsAllowed={moduleActionsAllowed}
                                            processamento={gruposEmProcessamento.get(Number(item.id))}
                                        />
                                    ))
                                ) : <EmptyTableRow colSpan={5} />
                            )}
                        </TableBody>
                    </Table>

                    <Pagination
                        numTotalItems={numTotalItems}
                        rowsPerPage={rowsPerPage}
                        page={page}
                    />
                </TableContainer>
            </Grid>
        </Grid>

        <GHEForm
            open={openForm}
            setOpen={setOpenForm}
            openCategoryDetailDialog={openCategoryDetailDialog}
            setOpenCategoryDetailDialog={setOpenCategoryDetailDialog}
            openDeleteDialog={openDeleteDialog}
            selectedGHE={selectedGHE}
            queryKey={queryKeyGHEsReport}
            // empresas={getEmpresasFilteredByModule('Setor', empresas, !selectedSector)}
            actionsAllowed={moduleActionsAllowed}
        />

        <ConfirmDialog
            title={`Excluir Grupo ${selectedGHE?.nome}`}
            description='Tem certeza que deseja excluir este grupo?'
            state={openDeleteDialog}
            goAction={() => handleDelete(selectedGHE.id)}
            handleClose={() => {
                setOpenDeleteDialog(false);
                setSelectedGHE(null);
                setOpenCategoryDetailDialog(false);
            }}
        />
    </>);
}

export default ListOfGHEs;